import type { ReportHistoryListItem } from '../api/reportHistory';
import type { ReportVersionRow } from '../api/reportVersions';
import { PIPEFITTER_REPORT_TYPES } from '../constants/reportVersionTypes';
import { pickReportFileRefByType } from './normalizeReportFileUrl';

export type PipefitterGroupedReportType =
  | typeof PIPEFITTER_REPORT_TYPES.PIPE_DATA
  | typeof PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY;

export interface GroupedReportEntry {
  reportType: PipefitterGroupedReportType;
  docId: number;
  fileUrl: string;
  fileName: string;
  generatedAt: Date;
  uploadedBy: string | null;
  pipeCount: number;
  pipeIds: string[];
  historyId?: number;
}

export interface ReportHistoryGroup {
  key: string;
  pipeCount: number;
  pipeIds: string[];
  generatedAt: Date;
  primaryType: PipefitterGroupedReportType;
  pipeData: GroupedReportEntry | null;
  pipeCompatibility: GroupedReportEntry | null;
}

function makePipeGroupKey(pipeIds: string[], soloId: string): string {
  if (pipeIds.length > 0) {
    return `pipes:${[...pipeIds].sort().join(',')}`;
  }
  return `solo:${soloId}`;
}

function toDate(value: string | Date | null | undefined): Date {
  if (value instanceof Date) return value;
  if (!value) return new Date(0);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

function pickNewer(
  existing: GroupedReportEntry | null,
  candidate: GroupedReportEntry
): GroupedReportEntry {
  if (!existing) return candidate;
  return candidate.generatedAt >= existing.generatedAt ? candidate : existing;
}

function historyToEntry(
  item: ReportHistoryListItem,
  version?: ReportVersionRow | null
): GroupedReportEntry | null {
  const reportType: PipefitterGroupedReportType =
    item.reportType === PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY
      ? PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY
      : PIPEFITTER_REPORT_TYPES.PIPE_DATA;

  const docId = version?.docId ?? item.id;
  const typeFileRef =
    pickReportFileRefByType(item.fileRefs, reportType) ??
    pickReportFileRefByType(item.fileUrl, reportType);
  const fileUrl =
    (version?.fileUrl && version.fileUrl.trim()) ||
    typeFileRef?.fileUrl ||
    (item.fileUrl && item.fileUrl.trim()) ||
    '';
  const fileName =
    (version?.fileName && version.fileName.trim()) ||
    typeFileRef?.fileName ||
    item.reportName ||
    'Report.pdf';
  const hasReportData = Boolean(item.reportData);
  if (!docId && !fileUrl && !hasReportData) return null;

  const pipeIds = item.pipeIds?.length
    ? item.pipeIds
    : version?.pipeIds?.length
      ? version.pipeIds
      : [];

  return {
    reportType,
    docId: version?.docId ?? item.id,
    fileUrl,
    fileName: version?.fileName?.trim() || fileName,
    generatedAt: toDate(version?.uploadDate ?? item.generatedAt),
    uploadedBy: version?.uploadedBy ?? item.generatedBy ?? null,
    pipeCount: item.pipeCount ?? version?.pipeCount ?? pipeIds.length,
    pipeIds,
    historyId: item.id,
  };
}

function versionToEntry(
  row: ReportVersionRow,
  reportType: PipefitterGroupedReportType
): GroupedReportEntry {
  const pipeIds = row.pipeIds ?? [];
  return {
    reportType,
    docId: row.docId,
    fileUrl: row.fileUrl,
    fileName: row.fileName,
    generatedAt: toDate(row.uploadDate),
    uploadedBy: row.uploadedBy,
    pipeCount: row.pipeCount ?? pipeIds.length,
    pipeIds,
  };
}

function matchVersionToHistory(
  history: ReportHistoryListItem,
  versions: ReportVersionRow[]
): ReportVersionRow | undefined {
  if (history.fileUrl) {
    const byUrl = versions.find((v) => v.fileUrl && v.fileUrl === history.fileUrl);
    if (byUrl) return byUrl;
  }

  const byId = versions.find((v) => v.docId === history.id);
  if (byId) return byId;

  const histTime = toDate(history.generatedAt).getTime();
  const candidates = versions.filter((v) => {
    if (
      history.pipeCount != null &&
      v.pipeCount != null &&
      history.pipeCount !== v.pipeCount
    ) {
      return false;
    }
    const vt = toDate(v.uploadDate).getTime();
    return Math.abs(vt - histTime) < 5 * 60 * 1000;
  });

  candidates.sort((a, b) => {
    const da = Math.abs(toDate(a.uploadDate).getTime() - histTime);
    const db = Math.abs(toDate(b.uploadDate).getTime() - histTime);
    return da - db;
  });

  return candidates[0];
}

export function buildReportHistoryGroups(
  histories: ReportHistoryListItem[],
  pipeDataVersions: ReportVersionRow[],
  compatibilityVersions: ReportVersionRow[]
): ReportHistoryGroup[] {
  const usedVersionDocIds = new Set<number>();
  const entries: GroupedReportEntry[] = [];

  for (const history of histories) {
    const versions =
      history.reportType === PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY
        ? compatibilityVersions
        : pipeDataVersions;
    const version = matchVersionToHistory(history, versions);
    if (version) usedVersionDocIds.add(version.docId);
    const entry = historyToEntry(history, version);
    if (entry) entries.push(entry);
  }

  const addOrphanVersions = (
    rows: ReportVersionRow[],
    reportType: PipefitterGroupedReportType
  ) => {
    for (const row of rows) {
      if (!row.docId || usedVersionDocIds.has(row.docId)) continue;
      if (!row.fileUrl) continue;
      entries.push(versionToEntry(row, reportType));
    }
  };

  addOrphanVersions(pipeDataVersions, PIPEFITTER_REPORT_TYPES.PIPE_DATA);
  addOrphanVersions(compatibilityVersions, PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY);

  const groupMap = new Map<string, ReportHistoryGroup>();

  for (const entry of entries) {
    const key = makePipeGroupKey(entry.pipeIds, `${entry.reportType}:${entry.docId}`);
    const existing = groupMap.get(key);
    const pipeCount = entry.pipeCount || entry.pipeIds.length;
    const pipeIds = entry.pipeIds;

    if (!existing) {
      groupMap.set(key, {
        key,
        pipeCount,
        pipeIds,
        generatedAt: entry.generatedAt,
        primaryType: entry.reportType,
        pipeData:
          entry.reportType === PIPEFITTER_REPORT_TYPES.PIPE_DATA ? entry : null,
        pipeCompatibility:
          entry.reportType === PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY ? entry : null,
      });
      continue;
    }

    if (entry.generatedAt > existing.generatedAt) {
      existing.generatedAt = entry.generatedAt;
      existing.primaryType = entry.reportType;
    }

    if (!existing.pipeCount && pipeCount) existing.pipeCount = pipeCount;
    if (!existing.pipeIds.length && pipeIds.length) existing.pipeIds = pipeIds;

    if (entry.reportType === PIPEFITTER_REPORT_TYPES.PIPE_DATA) {
      existing.pipeData = pickNewer(existing.pipeData, entry);
    } else {
      existing.pipeCompatibility = pickNewer(existing.pipeCompatibility, entry);
    }
  }

  return Array.from(groupMap.values()).sort(
    (a, b) => b.generatedAt.getTime() - a.generatedAt.getTime()
  );
}

export function normalizePipeIdSetKey(pipeIds: Array<string | number>): string {
  return [...pipeIds]
    .map(String)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .join(',');
}

export function pipeIdSetsMatch(
  left: Array<string | number>,
  right: Array<string | number>
): boolean {
  if (!left.length || !right.length) return false;
  return normalizePipeIdSetKey(left) === normalizePipeIdSetKey(right);
}

export function findGroupMatchingPipeIds(
  groups: ReportHistoryGroup[],
  selectedPipeStatusIds: Array<string | number>
): ReportHistoryGroup | null {
  if (!selectedPipeStatusIds.length) return null;
  return groups.find((group) => pipeIdSetsMatch(group.pipeIds, selectedPipeStatusIds)) ?? null;
}
